import { Button, Spinner, Text, tokens } from "@fluentui/react-components";
import { useMemo } from "react";

import { EmptyState } from "@/components/EmptyState";
import { ErrorState } from "@/components/ErrorState";
import { DocumentRow } from "@/features/documents/DocumentRow";
import { EmailDetailDrawer } from "@/features/documents/EmailDetailDrawer";
import { useDocuments } from "@/features/documents/useDocuments";
import { useDebouncedValue } from "@/lib/useDebouncedValue";
import { useFilterStore } from "@/store/filterStore";
import { useSelectionStore } from "@/store/selectionStore";

const TEXT_DEBOUNCE_MS = 300;

export function DocumentList() {
  const subject = useFilterStore((s) => s.subject);
  const from = useFilterStore((s) => s.from);
  const documentType = useFilterStore((s) => s.documentType);
  const listTab = useFilterStore((s) => s.listTab);
  const dateFrom = useFilterStore((s) => s.dateFrom);
  const dateTo = useFilterStore((s) => s.dateTo);
  const sort = useFilterStore((s) => s.sort);
  const tradeKey = useFilterStore((s) => s.tradeKey);
  const correctedOnly = useFilterStore((s) => s.correctedOnly);
  const mode = useFilterStore((s) => s.mode);

  const debSubject = useDebouncedValue(subject, TEXT_DEBOUNCE_MS);
  const debFrom = useDebouncedValue(from, TEXT_DEBOUNCE_MS);
  const debTradeKey = useDebouncedValue(tradeKey, TEXT_DEBOUNCE_MS);

  const filters = useMemo(
    () => ({
      subject: debSubject,
      from: debFrom,
      documentType,
      listTab,
      dateFrom,
      dateTo,
      sort,
      tradeKey: debTradeKey,
      correctedOnly,
      mode,
    }),
    [
      debSubject,
      debFrom,
      documentType,
      listTab,
      dateFrom,
      dateTo,
      sort,
      debTradeKey,
      correctedOnly,
      mode,
    ],
  );

  const query = useDocuments(filters);
  const openEmail = useSelectionStore((s) => s.open);

  if (query.isLoading) {
    return (
      <div style={{ padding: 48, textAlign: "center" }}>
        <Spinner label="Loading documents…" />
      </div>
    );
  }
  if (query.isError) {
    return (
      <ErrorState
        title="Failed to load documents"
        message={query.error instanceof Error ? query.error.message : "Unknown error"}
      />
    );
  }

  const docs = query.data?.pages.flatMap((p) => p.documents) ?? [];
  if (docs.length === 0) {
    return <EmptyState message="No documents match these filters." />;
  }

  return (
    <>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 8,
          padding: 16,
        }}
      >
        {docs.map((doc) => (
          <div key={doc.id} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <button
              type="button"
              onClick={() =>
                doc.emailMessageIdHash && openEmail(doc.emailMessageIdHash)
              }
              disabled={!doc.emailMessageIdHash}
              title="Open email details"
              style={{
                all: "unset",
                cursor: doc.emailMessageIdHash ? "pointer" : "default",
                padding: "2px 4px",
                borderRadius: 4,
                alignSelf: "flex-start",
                maxWidth: "100%",
              }}
              onMouseEnter={(e) => {
                if (doc.emailMessageIdHash) {
                  e.currentTarget.style.background = tokens.colorNeutralBackground1Hover;
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "transparent";
              }}
            >
              <Text
                size={200}
                style={{ color: tokens.colorNeutralForeground2 }}
                truncate
                block
              >
                {doc.emailSubject || "(no subject)"} ·{" "}
                {doc.emailFrom || "(unknown sender)"}
              </Text>
            </button>
            <DocumentRow doc={doc} />
          </div>
        ))}
        {query.hasNextPage && (
          <div style={{ alignSelf: "center", paddingTop: 8 }}>
            <Button
              onClick={() => query.fetchNextPage()}
              disabled={query.isFetchingNextPage}
              appearance="secondary"
            >
              {query.isFetchingNextPage ? "Loading…" : "Load more"}
            </Button>
          </div>
        )}
      </div>
      <EmailDetailDrawer />
    </>
  );
}
